/**
 * @file     usePromptSignals.ts
 * @purpose  The visitor signals a prompt surface needs (has the app, which
 *           first-time-buyer signal matched, already booked a call), read
 *           once the prompt timing gate has passed and never before.
 */
'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import type { HasAppSignal } from '@/core/config/prompts';
import { usePromptTiming } from '@/core/overlays/OverlayPriorityProvider';
import { useAuth } from '@/hooks/useAuth';
import {
  fetchVisitorSignals,
  firstTimeBuyerSignal,
  hasApp as resolveHasApp,
  hasBookedCall as resolveHasBookedCall,
  readClientFtbFacts,
  type FtbSignal,
} from '@/services/prompts/visitorSignals';

export interface PromptSignals {
  /** False until the timing gate passed and the signals were read. */
  loaded: boolean;
  signedIn: boolean;
  hasApp: HasAppSignal;
  ftbSignal: FtbSignal | null;
  bookedCall: boolean;
}

const NOT_LOADED: PromptSignals = { loaded: false, signedIn: false, hasApp: 'unknown', ftbSignal: null, bookedCall: false };

/**
 * Read once per page after the gate opens; the server half is cached per
 * session. `immediate` is for in-page surfaces (the Book a call link) that are
 * not passive prompts and should not wait for engagement.
 */
export function usePromptSignals(options?: { immediate?: boolean }): PromptSignals {
  const immediate = options?.immediate ?? false;
  const { ready } = usePromptTiming();
  const { user, isAuthenticated, isLoading } = useAuth();
  const pathname = usePathname() ?? '';
  const [signals, setSignals] = useState<PromptSignals>(NOT_LOADED);
  const [armed, setArmed] = useState(false);

  // The gate can close again (a dialog opens); signals already read stay read.
  useEffect(() => {
    if (ready || immediate) setArmed(true);
  }, [ready, immediate]);

  useEffect(() => {
    if (!armed || isLoading) return;
    let cancelled = false;
    const signedIn = isAuthenticated && !!user;
    const server = signedIn && user ? fetchVisitorSignals(user.id) : Promise.resolve(null);
    void server.then((s) => {
      if (cancelled) return;
      setSignals({
        loaded: true,
        signedIn,
        hasApp: resolveHasApp(s, signedIn),
        ftbSignal: firstTimeBuyerSignal(readClientFtbFacts(pathname, s?.ftb ?? null)),
        bookedCall: resolveHasBookedCall(s),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [armed, isLoading, isAuthenticated, user, pathname]);

  return signals;
}
